import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/utils';

// A small hover tooltip (Nic 2026-10-09: react-bootstrap's flashed a ghost
// at the corner of the screen, then darted to the cursor). This one is drawn
// ONCE, already in place: its spot comes from the hovered element alone
// (centered above it, or below when there's no room), and the tip centers
// itself with a CSS transform, so nothing is measured or moved after it
// appears. It waits a beat before showing, so sweeping the mouse across a
// board doesn't flicker a trail of tips. position: fixed, so it never
// changes the page height.
const DELAY_MS = 250;
const GAP = 6;
const MAX_W = 260;
const EDGE = 8;
// Room needed above the target before we flip below it.
const ROOM_ABOVE = 64;

// placement 'below' (2026-10-20): for targets at the top of the page, where
// a tall tip opened above would run off the screen.
const placeFor = (el, placement = 'auto') => {
  const r = el.getBoundingClientRect();
  const above = placement !== 'below' && r.top >= ROOM_ABOVE;
  const half = MAX_W / 2;
  const center = r.left + r.width / 2;
  return {
    above,
    top: above ? r.top - GAP : r.bottom + GAP,
    left: Math.min(Math.max(center, EDGE + half), window.innerWidth - EDGE - half),
  };
};

const HoverTip = ({ text, children, className, disabled = false, placement = 'auto' }) => {
  const anchorRef = useRef(null);
  const timer = useRef(null);
  const [place, setPlace] = useState(null);

  const show = () => {
    if (disabled || !text) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (anchorRef.current) setPlace(placeFor(anchorRef.current, placement));
    }, DELAY_MS);
  };
  const hide = () => {
    clearTimeout(timer.current);
    setPlace(null);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (disabled) hide();
  }, [disabled]);
  // Scrolling moves the target: just close.
  useEffect(() => {
    if (!place) return undefined;
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [place]);

  return (
    <span
      ref={anchorRef}
      className={cn('inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {place
        ? createPortal(
            // Outer: the anchor point. Inner: the bubble, centered on it.
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[1080]"
              style={{ top: place.top, left: place.left, width: 0, height: 0 }}
            >
              <div
                className="absolute w-max max-w-[260px] rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium leading-snug text-white shadow-lg dark:bg-slate-700"
                style={{
                  left: 0,
                  transform: place.above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
                }}
              >
                {text}
              </div>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
};

export default HoverTip;
